import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faEdit, faTrash, faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import roleApi from '../../api/roleApi';

const Roles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [roleToDeleteId, setRoleToDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchRoles = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      // Assuming backend API returns data ordered by updated_at descending by default
      const response = await roleApi.getRoles({ page, perPage, search, includeTrashed: false });
      setRoles(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch Roles:', err);
      setError('Failed to load Roles. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchRoles, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleAddRole = () => {
    navigate('/roles/create');
  };

  const handleGoToTrash = () => {
    navigate('/roles/trash');
  };

  const handleEditRole = (id) => {
    navigate(`/roles/${id}/edit`);
  };

  const handleDeleteClick = (id) => {
    setRoleToDeleteId(id);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsModalOpen(false);
    if (roleToDeleteId) {
      setLoading(true);
      setError(null);
      try {
        await roleApi.trashRole(roleToDeleteId);
        await fetchRoles(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to trash Role:', err);
        setError('Failed to move Role to trash. Please try again.');
      } finally {
        setLoading(false);
        setRoleToDeleteId(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setRoleToDeleteId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6"> {/* Modified for responsiveness */}
        <h1 className="text-3xl font-bold text-text-white mb-4 sm:mb-0">Roles Management</h1> {/* Added mb-4 for mobile spacing */}
        <div className="flex space-x-4">
          <Button
            onClick={handleAddRole}
            className="bg-accent-red hover:bg-red-700 text-white"
          >
            Add New Role
          </Button>
          <Button
            onClick={handleGoToTrash}
            className="bg-gray-600 hover:bg-gray-700 text-white"
            icon={faTrashAlt}
            tooltipId="trash-roles-tooltip"
            tooltipContent="View Trashed Roles"
          >
            Trash
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Roles"
          id="search-roles"
          name="search-roles"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by role name..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Name</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Description</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Updated At</th> {/* New column */}
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.length > 0 ? (
                  roles.map((role) => (
                    <tr key={role.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{role.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{role.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{role.description}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {new Date(role.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td> {/* Display updated_at */}
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleEditRole(role.id)}
                          className="text-yellow-500 hover:text-yellow-600 mr-3"
                          icon={faEdit}
                          tooltipId={`edit-role-tooltip-${role.id}`}
                          tooltipContent="Edit"
                        />
                        <Button
                          onClick={() => handleDeleteClick(role.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrash}
                          tooltipId={`delete-role-tooltip-${role.id}`}
                          tooltipContent="Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-sm text-text-gray">No Roles found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to move this Role to trash?"
      />
    </div>
  );
};

export default Roles;